import clsx from 'clsx'
import Swal from 'sweetalert2'
import { toast } from 'react-toastify'
import PropTypes from 'prop-types'
import ReactTooltip from 'react-tooltip'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import Btn from '../../../../UI/Btn'
import MiniHeader from '../../../../UI/MiniHeader'
import Search from '../../../../UI/Search'
import BtnFilter from '../../../../UI/BtnFilter'
import FilterPanel from '../../../../UI/FilterPanel'
import useFilters from '../../../../UI/FilterPanel/useFilters'
import {
	newsAuthorField,
	newsBranchField,
	newsCompanyTypeField,
	newsRolesField,
	newsSchoolTypeField,
	newsStateField,
	newsStatusField,
	newsTypeField
} from '../../../../UI/FilterPanel/fields'

import http from '../../../../shared/http'
import { StateOptions, TApi, TCompanies, TLanguages, TRoles } from '../../../../shared/const'
import eventEmitter, { types } from '../../../../shared/eventEmitter'
import { stateLabel } from '../../../../shared/helpers'
import { isAlumni } from '../../../../shared/alumni'
import { newsAudienceFacets } from '../../../../shared/newsAudience'
import useDebouncedValue from '../../../../shared/useDebouncedValue'
import useScrollTopOnTabChange from '../../../../shared/useScrollTopOnTabChange'

import injectRole from '../../../HOC/injectRole'
import VisibilityAccess from '../../../VisibilityAccess'
import NewsCard from './components/NewsCard'
import NewsTimeline from './components/NewsTimeline'

import styles from './style.module.scss'

/*
 * Veranstaltungen & Neuigkeiten.
 *
 * Admins sehen zwei Register: eine Zeitachse ("wann laeuft was") und die
 * vollstaendige Liste ("welche gibt es"). Das sind zwei verschiedene Fragen —
 * die Liste beantwortet die zweite gut und die erste gar nicht.
 *
 * Beide arbeiten auf demselben, einmal geladenen Bestand. Die Liste blaettert
 * nicht mehr: Bei rund 25 Nachrichten kostet das Laden nichts, und Filter, die
 * nur die aktuelle Seite durchsuchen, sind eine Falle — man haelt fuer
 * "nichts gefunden", was in Wahrheit "nicht auf dieser Seite" heisst.
 *
 * Gefiltert und gesucht wird ueber die Leiste, die von rechts hereinfaehrt
 * (UI/FilterPanel) — dieselbe wie in allen anderen Listen. Sie gilt fuer beide
 * Register: Ein Filter, der beim Wechsel des Registers verfaellt, waere eine
 * Ueberraschung — und die Zeitachse braucht ihn genauso: "Was laeuft im Herbst
 * fuer die Steiermark?" ist erst mit Einschraenkung eine beantwortbare Frage.
 *
 * Gefiltert wird im Browser: Der gesamte Bestand liegt ohnehin hier (die Liste
 * blaettert nicht), es wird also nichts nachgeladen.
 *
 * Die Zielgruppe steht seit 26.08.2026 in beiden Registern: in der Liste als
 * Chips im Fuss der Karte, auf der Zeitachse in einer Klammer hinter dem Titel
 * ("fuer alle", "nur fuer Lehrer:innen · W"). Ohne sie sehen zwei Balken gleich
 * aus, von denen der eine bundesweit an alle geht und der andere an die
 * Lehrkraefte eines Bundeslandes.
 *
 * Schueler:innen und Lehrkraefte sehen dieselben Karten ohne die Angaben der
 * Redaktion. Vorher stand hier eine zweispaltige Tabelle: Titel links, ein
 * abgeschnittener Absatz rechts, keine Art, kein Datum, kein Ort — bei einer
 * Veranstaltung also gerade das nicht, wonach man sucht. Der Server liefert
 * ihnen ohnehin nur, was gerade laeuft.
 *
 * Seit 08.08.2026 haben auch sie Suche und Filter in der Kopfleiste — dieselbe
 * Anordnung wie in jeder anderen Ansicht (ganz rechts der Filter, davor die
 * Suche). Zu filtern gibt es fuer sie genau eine Sache: die **Art**. Alles
 * andere (Bundesland, Rolle, Schultyp, Unternehmensform, Branche, Autor:in) ist
 * die Zielgruppenangabe der Redaktion — danach hat der Server bereits
 * ausgewaehlt, was sie ueberhaupt zu sehen bekommen.
 *
 * Geblaettert wird dabei nicht mehr: Ein Filter, der nur die aktuelle Seite
 * durchsucht, ist dieselbe Falle wie oben — und was gerade laeuft, sind
 * ueblicherweise ein paar Dutzend Nachrichten, keine Hunderte.
 */

const TAB_TIMELINE = 'timeline'
const TAB_LIST = 'list'

// Ohne Blaettern muss alles in einem Zug kommen. Der Server laesst bis 500 zu.
const LIMIT = 500

// Fuer Schueler:innen und Lehrkraefte laesst er weniger zu (Lehrkraft: 100).
const LIMIT_LESEND = 100

function News(props) {
	const { t, i18n } = useTranslation()
	const [news, setNews] = useState([])
	const [countNews, setCountNews] = useState(0)
	const [tab, setTab] = useState(TAB_TIMELINE)
	const [search, setSearch] = useState('')
	const [showFilter, setShowFilter] = useState(false)

	const tabsRef = useRef(null)
	useScrollTopOnTabChange(tab, tabsRef)

	// Bearbeiten darf die Verwaltung. Alumni (#213) lesen dieselbe Ansicht mit
	// Zeitleiste und Zielgruppen, aber ohne Anlegen, Bearbeiten und Löschen.
	const mayEdit = props.role === TRoles.ADMIN || props.role === TRoles.FEDERAL_ADMIN
	const isAdmin = mayEdit || isAlumni(props.role)
	// Anheften darf nur die Bundesadministration (Anforderung #164): Eine
	// Landesadministration sieht nur ihr Bundesland, ihre Anheftung wirkte aber
	// auch auf bundesweite Nachrichten. Der Server haelt dieselbe Regel.
	const darfAnheften = props.role === TRoles.FEDERAL_ADMIN
	const limit = isAdmin ? LIMIT : LIMIT_LESEND

	const getNameApiGetNews = () => {
		const api = {
			[TRoles.ADMIN]: TApi.ADMIN_GET_NEWS,
			[TRoles.FEDERAL_ADMIN]: TApi.ADMIN_GET_NEWS,
			[TRoles.ALUMNI]: TApi.ADMIN_GET_NEWS,
			[TRoles.TEACHER]: TApi.TEACHER_GET_NEWS,
			[TRoles.STUDENT]: TApi.STUDENT_GET_NEWS
		}

		return api[props.role]
	}

	const fetchNews = () => {
		const api = getNameApiGetNews()

		/*
		 * Beim Neuladen der Seite steht die Rolle erst nach dem Profilabruf fest,
		 * und ohne Rolle gibt es keine Adresse. Die Anfrage ging dann an die
		 * Wurzel — im Betrieb beantwortet der Webserver die mit der Startseite,
		 * also mit HTML statt Daten. `data.rows` war undefiniert, die Liste damit
		 * auch, und der Aufbau der Filterwerte riss die ganze Anwendung mit
		 * (weisse Seite). Lokal fiel das nie auf, weil das Backend dort unter
		 * einer anderen Adresse liegt und schlicht mit 404 antwortet.
		 *
		 * Sobald die Rolle da ist, laedt der Effekt weiter unten erneut.
		 */
		if (!api) return

		/*
		 * Die Verwaltung sortiert nach dem Anzeigebeginn — sie pflegt den
		 * Bestand und sucht darin. Schueler:innen und Lehrkraefte bekommen die
		 * Lesereihenfolge des Servers ('presentation'): angeheftete zuerst,
		 * dann die naechsten Termine, dann das zuletzt Veroeffentlichte. Sie
		 * steht im Backend an einer Stelle und gilt dort auch fuer die fuenf
		 * Kacheln des Dashboards — sonst zeigten die beiden Orte dieselben
		 * Nachrichten in verschiedener Folge.
		 */
		http.get(api, {
			params: {
				limit,
				offset: 0,
				order: isAdmin ? 'startDate' : 'presentation',
				direction: 'desc',
				filter: JSON.stringify({})
			}
		}).then(({ data }) => {
			setNews(data.rows || [])
			setCountNews(data.count || 0)
		})
	}

	const handleDeleteNews = async id => {
		const { isConfirmed } = await Swal.fire({
			title: t('Delete message?'),
			text: t('The message will no longer be shown to anyone. This cannot be undone.'),
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: t('Remove'),
			cancelButtonText: t('Cancel'),
			confirmButtonColor: '#d33'
		})

		if (!isConfirmed) return

		return http.delete(TApi.ADMIN_DELETE_NEWS(id)).then(() => fetchNews())
	}

	const handleDone = () => fetchNews()

	/*
	 * Aktiv ↔ Entwurf per Mausklick (Anforderung #164) — ohne das Formular zu
	 * oeffnen und ohne am Datum zu drehen. Ein Entwurf bleibt fuer
	 * Schueler:innen und Lehrkraefte verborgen; beim Aktivieren holt der Server
	 * die Mitteilung nach, falls es noch keine gibt.
	 */
	const handleToggleDraft = (id, isDraft) =>
		http.put(TApi.ADMIN_NEWS_DRAFT(id), { isDraft }).then(() => {
			toast.success(t(isDraft ? 'The message was set to draft.' : 'The message is now active.'))
			fetchNews()
		})

	/*
	 * Oben anheften (Anforderung #164): Die Nachricht steht bei Schueler:innen
	 * und Lehrkraeften vor allen anderen — in der Liste wie auf dem Dashboard.
	 * Sind mehrere angeheftet, steht die zuletzt angeheftete oben.
	 */
	const handleTogglePin = (id, isPinned) =>
		http.put(TApi.ADMIN_NEWS_PIN(id), { isPinned }).then(() => {
			toast.success(t(isPinned ? 'The message is now pinned to the top.' : 'The message is no longer pinned.'))
			fetchNews()
		})

	// Die Karten reichen die Kennung durch, die Zeitachse den ganzen Datensatz.
	const feedFinden = feed => (typeof feed === 'object' ? feed : news.find(i => i.id === feed))

	const handleEditNews = feed => eventEmitter.emit(types.openAddFeedModal, feedFinden(feed), handleDone)

	/*
	 * Duplizieren: dasselbe Fenster, dieselben Werte — aber ohne Kennung, also
	 * als neue Nachricht. Gebraucht wird das fuer dieselbe Veranstaltung in
	 * einem weiteren Bundesland und fuer den Aufruf, der jedes Jahr wiederkehrt;
	 * beides war bisher Abtippen aus dem geoeffneten Original.
	 */
	const handleDuplicateNews = feed =>
		eventEmitter.emit(types.openAddFeedModal, feedFinden(feed), handleDone, { kopie: true })

	// Admins bearbeiten, Schueler:innen und Lehrkraefte lesen. Fuer die
	// Administration ist die Nachricht ein Datensatz, den sie pflegt — ein
	// Zwischenschritt "erst ansehen, dann bearbeiten" waere nur ein Klick mehr.
	const handleOpenNews = item => (mayEdit ? handleEditNews(item) : eventEmitter.emit(types.openDetailFeedModal, item))

	const branchLabel = value => {
		const option = props.companyBranch?.find(v => v.value_EN === value)
		if (!option) return value

		return i18n.language === TLanguages.DE ? option.value_DE : option.value_EN
	}

	/*
	 * Die Auswahlwerte der Zielgruppen-Filter kommen aus den Stammdaten — also
	 * alle neun Bundeslaender, beide Rollen, alle Schultypen, alle Branchen,
	 * auch wenn keine Nachricht sie traegt.
	 *
	 * Bis 26.08.2026 wurden sie aus dem Bestand gesammelt, mit der Begruendung,
	 * ein Filter auf Unbenutztes fuehre nur zu leeren Listen. Fuer die
	 * Zielgruppen stimmt das nicht: Eine Nachricht **ohne** Eintrag gilt fuer
	 * alle und passt damit zu jeder Auswahl. "Was sieht die Steiermark?" ist
	 * deshalb eine sinnvolle Frage, gerade wenn keine Nachricht ausdruecklich
	 * fuer die Steiermark gesetzt ist — die Antwort ist dann "alles Allgemeine".
	 * Als Auswahl gab es das Bundesland aber gar nicht, die Frage liess sich
	 * nicht stellen.
	 *
	 * Die Autor:innen bleiben aus dem Bestand: Dort ist die Stammdatenliste
	 * jedes Konto der Verwaltung, und wer nie eine Nachricht geschrieben hat,
	 * gehoert nicht in den Filter.
	 */
	const filterOptions = useMemo(() => {
		const nachLabel = (a, b) => a.label.localeCompare(b.label)

		const autoren = new Set()
		news.forEach(item => item.creator?.email && autoren.add(item.creator.email))

		return {
			states: StateOptions.map(option => ({ value: option.value, label: stateLabel(option.value) })).sort(
				nachLabel
			),
			// Dieselben beiden wie im Dialog: Nachrichten gehen an
			// Schueler:innen und Lehrkraefte.
			roles: [TRoles.STUDENT, TRoles.TEACHER].map(rolle => ({ value: rolle, label: t(rolle) })),
			schoolType: (props.schoolTypes || [])
				.map(eintrag => ({ value: eintrag.value, label: eintrag.value }))
				.sort(nachLabel),
			companyTypes: Object.values(TCompanies).map(wert => ({ value: wert, label: wert })),
			companyBranch: (props.companyBranch || [])
				.map(option => ({ value: option.value_EN, label: branchLabel(option.value_EN) }))
				.sort(nachLabel),
			authors: [...autoren].sort()
		}
		/* eslint-disable-next-line */
	}, [news, i18n.language, props.companyBranch, props.schoolTypes])

	/*
	 * Nur die tatsaechlich gesetzten Einschraenkungen. Ein leeres Ergebnis
	 * heisst "an alle" — die Karte schreibt dann einen Satz hin statt fuenf Mal
	 * "Alle", die Zeitachse "(fuer alle)" in die Klammer.
	 *
	 * Gerechnet wird in shared/newsAudience: Karte und Zeitachse sollen
	 * dieselbe Angabe in derselben Reihenfolge zeigen.
	 *
	 * `totals` sind dieselben Stammdaten wie im Filter — daran erkennt die
	 * Angabe ein Feld, in dem *alle* Werte angehakt sind. Das ist im Bestand
	 * die Regel und keine Ausnahme (alle neun Schultypen, alle neunzehn
	 * Branchen) und schraenkt nichts ein.
	 */
	const totals = useMemo(
		() => ({
			roles: filterOptions.roles.map(option => option.value),
			states: filterOptions.states.map(option => option.value),
			schoolType: filterOptions.schoolType.map(option => option.value),
			companyTypes: filterOptions.companyTypes.map(option => option.value),
			companyBranch: filterOptions.companyBranch.map(option => option.value)
		}),
		[filterOptions]
	)

	const audienceFacets = useCallback(
		item => newsAudienceFacets(item, { t, branchLabel, totals }),
		/* eslint-disable-next-line */
		[t, i18n.language, props.companyBranch, totals]
	)

	/*
	 * Die Felder der Leiste. Reihenfolge wie bisher in der Leiste im
	 * Seiteninhalt. Lesende bekommen nur die Art: Die uebrigen Felder sind die
	 * Zielgruppenangabe der Redaktion, nach der der Server fuer sie ohnehin
	 * schon ausgewaehlt hat — ein Filter darauf koennte nur alles oder nichts.
	 */
	const fields = useMemo(
		() =>
			isAdmin
				? [
						newsStatusField(t),
						newsTypeField(t),
						newsStateField(t, filterOptions.states),
						newsRolesField(t, filterOptions.roles),
						newsSchoolTypeField(t, filterOptions.schoolType),
						newsCompanyTypeField(t, filterOptions.companyTypes),
						newsBranchField(t, filterOptions.companyBranch),
						// Verfasser:innen stehen als E-Mail-Adresse — die bekommen
						// Alumni nicht, der Filter bliebe leer.
						mayEdit && newsAuthorField(t, filterOptions.authors)
				  ].filter(Boolean)
				: [newsTypeField(t)],
		[t, isAdmin, mayEdit, filterOptions]
	)

	/*
	 * Eine Landesadministration findet ihr eigenes Bundesland vorgewaehlt vor.
	 *
	 * Sie pflegt und liest die Nachrichten ihres Landes; der ungefilterte
	 * Bestand ist bundesweit und beantwortet ihre Frage nicht. Der Filter ist
	 * eine Vorauswahl und keine Sperre: Er steht sichtbar in der Kopfleiste
	 * ("Filter (1)"), laesst sich aendern und leeren, und wer ihn geleert hat,
	 * findet ihn beim naechsten Mal leer vor.
	 *
	 * Die Bundesadministration bekommt keine — fuer sie ist der ganze Bestand
	 * die Arbeitsgrundlage.
	 *
	 * Zur Filterregel: Eine Nachricht ohne Bundesland gilt fuer alle und steht
	 * deshalb auch hier. Die Auswahl beantwortet "was ist in Tirol zu sehen",
	 * nicht "was ist ausdruecklich fuer Tirol gesetzt".
	 */
	const defaults = useMemo(() => {
		if (props.role !== TRoles.ADMIN || !props.eigenesLand) return undefined

		return { states: [{ value: props.eigenesLand, label: stateLabel(props.eigenesLand) }] }
	}, [props.role, props.eigenesLand])

	// Eigener Merkzettel je Sicht: Die Redaktion filtert nach anderen Feldern als
	// die Lesenden, ein gemeinsamer Schluessel truege deren Auswahl herueber.
	const filters = useFilters({ storageKey: isAdmin ? 'news' : 'news-lesend', fields, defaults })
	const debouncedSearch = useDebouncedValue(search)

	const gefiltert = useMemo(() => {
		const gewaehlt = key => filters.query[key] || []

		const trifftListe = (auswahl, werte) => {
			if (!auswahl.length) return true
			// Ohne Eintrag gilt die Nachricht fuer alle — dann passt sie auch zu
			// jeder Auswahl.
			if (!werte || !werte.length) return true

			return auswahl.some(wert => werte.includes(wert))
		}

		// Ein einzelner Wert am Datensatz: die Nachricht muss einem der
		// gewaehlten entsprechen.
		const trifftWert = (auswahl, wert) => !auswahl.length || auswahl.includes(wert)

		const begriff = debouncedSearch.trim().toLowerCase()

		return news.filter(item => {
			if (!trifftWert(gewaehlt('status'), item.status)) return false
			if (!trifftWert(gewaehlt('newsType'), item.newsType)) return false
			if (!trifftWert(gewaehlt('author'), item.creator?.email)) return false
			if (!trifftListe(gewaehlt('states'), item.states)) return false
			if (!trifftListe(gewaehlt('roles'), item.roles)) return false
			if (!trifftListe(gewaehlt('schoolType'), item.schoolType)) return false
			if (!trifftListe(gewaehlt('companyTypes'), item.companyTypes)) return false
			if (!trifftListe(gewaehlt('companyBranch'), item.companyBranch)) return false

			if (begriff) {
				const treffer = [item.title, item.description, item.place]
					.filter(Boolean)
					.some(feld => String(feld).toLowerCase().includes(begriff))
				if (!treffer) return false
			}

			return true
		})
	}, [news, filters.query, debouncedSearch])

	/*
	 * Angeheftete Nachrichten stehen auch in der Verwaltungsliste oben — sonst
	 * sieht die Redaktion nicht, was sie angeheftet hat, sondern muesste die
	 * Abzeichen in 26 Karten suchen. Die Zeitachse bleibt davon unberuehrt: Sie
	 * ordnet nach der Zeit, das ist ihr ganzer Zweck.
	 *
	 * Sortiert wird eine Kopie; `sort` arbeitet sonst auf dem Ergebnis des
	 * Filters und damit auf dem Zustand.
	 */
	const kartenReihenfolge = useMemo(
		() =>
			[...gefiltert].sort((a, b) => {
				const angeheftet = Number(Boolean(b.pinnedAt)) - Number(Boolean(a.pinnedAt))
				if (angeheftet) return angeheftet
				if (!a.pinnedAt || !b.pinnedAt) return 0

				// Beide angeheftet: die zuletzt angeheftete oben — dieselbe
				// Regel wie beim Server.
				return new Date(b.pinnedAt) - new Date(a.pinnedAt)
			}),
		[gefiltert]
	)

	// Die "+N"-Hinweise der Karten haengen an data-tip und stehen erst nach dem
	// Rendern im DOM.
	useEffect(() => {
		ReactTooltip.rebuild()
	}, [gefiltert])

	useEffect(() => {
		fetchNews()
		/* eslint-disable-next-line */
	}, [props.role])

	return (
		<div className={clsx(styles.root, { [styles.rootAdmin]: isAdmin })}>
			<MiniHeader className={styles.miniHeader} title={t('Events & News')} bg="#F8FAFF">
				{/* Ganz rechts der Filter, davor die Suche - so in jeder Ansicht.
				    Handlungen der Seite reihen sich davor ein. */}
				<div className={styles.headerContent}>
					<VisibilityAccess access={[TRoles.ADMIN, TRoles.FEDERAL_ADMIN]}>
						<Btn
							onClick={() => eventEmitter.emit(types.openAddFeedModal, null, handleDone)}
							iconSize={{ w: 10, h: 10 }}
							icon="arrow"
							iconColor="#fff"
							className={styles.createButton}
						>
							{t('Create message')}
						</Btn>
					</VisibilityAccess>
					<Search
						value={search}
						onChange={e => setSearch(e.target.value)}
						placeholder={t('Search messages...')}
						onSubmit={e => e.preventDefault()}
					/>
					<BtnFilter icon="arrow" onClick={() => setShowFilter(true)} count={filters.count}>
						{t('Filter')} ({filters.count})
					</BtnFilter>
				</div>
			</MiniHeader>

			{isAdmin ? (
				<div className={styles.listWrap}>
					<div className={styles.tabs} ref={tabsRef}>
						<button
							type="button"
							className={clsx(styles.tab, tab === TAB_TIMELINE && styles.tabActive)}
							onClick={() => setTab(TAB_TIMELINE)}
						>
							{t('Chronological')}
						</button>
						<button
							type="button"
							className={clsx(styles.tab, tab === TAB_LIST && styles.tabActive)}
							onClick={() => setTab(TAB_LIST)}
						>
							{t('All messages')}
							<span className={styles.tabCount}>{countNews}</span>
						</button>
					</div>

					{tab === TAB_TIMELINE ? (
						<NewsTimeline
							items={gefiltert}
							gesamt={news.length}
							onOpen={handleOpenNews}
							audienceOf={audienceFacets}
						/>
					) : (
						<>
							<p className={styles.counter}>
								{gefiltert.length === news.length
									? `${news.length} ${t('Messages')}`
									: `${gefiltert.length} ${t('of')} ${news.length} ${t('Messages')}`}
							</p>

							{gefiltert.length === 0 ? (
								<p className={styles.empty}>{t('No messages found.')}</p>
							) : (
								<div className={styles.cards}>
									{kartenReihenfolge.map(item => (
										<NewsCard
											key={item.id}
											item={item}
											facets={audienceFacets(item)}
											onOpen={handleOpenNews}
											onEdit={mayEdit ? handleEditNews : undefined}
											onDuplicate={mayEdit ? handleDuplicateNews : undefined}
											onToggleDraft={mayEdit ? handleToggleDraft : undefined}
											onTogglePin={darfAnheften ? handleTogglePin : undefined}
											onDelete={mayEdit ? handleDeleteNews : undefined}
										/>
									))}
								</div>
							)}
						</>
					)}
				</div>
			) : (
				<div className={clsx('container', styles.feed)}>
					{news.length === 0 ? (
						<p className={styles.empty}>{t('No news yet')}</p>
					) : (
						<>
							<p className={styles.counter}>
								{gefiltert.length === news.length
									? `${news.length} ${t('Messages')}`
									: `${gefiltert.length} ${t('of')} ${news.length} ${t('Messages')}`}
							</p>

							{gefiltert.length === 0 ? (
								<p className={styles.empty}>{t('No messages found.')}</p>
							) : (
								<div className={styles.cards}>
									{gefiltert.map(item => (
										<NewsCard key={item.id} item={item} onOpen={handleOpenNews} />
									))}
								</div>
							)}
						</>
					)}
				</div>
			)}

			<FilterPanel
				open={showFilter}
				onClose={() => setShowFilter(false)}
				fields={fields}
				values={filters.values}
				count={filters.count}
				onChange={filters.setField}
				onResetAll={filters.resetAll}
			/>
		</div>
	)
}

News.propTypes = {
	role: PropTypes.string.isRequired,

	// Bundesland der angemeldeten Person — nur eine Landesadministration hat
	// eines. Steht erst nach dem Profilabruf fest.
	eigenesLand: PropTypes.string
}

const mapStateToProps = state => ({
	companyBranch: state.options.branch,
	eigenesLand: state.user.profile?.state
})

export default connect(mapStateToProps)(injectRole(News))
